import { Model } from "@singularsystems/neo-core";
import { Views } from "@singularsystems/neo-react";
import { Audit } from '@singularsystems/neo-react-services';
import { NotificationDuration } from "../../App/Models/NotificationDuration";
import { AppService, DomainTypes, Types } from "../DomainTypes";
import { ConfigValues } from "../Models/Config/TenantConfig";
import { IPreAuthorisable } from "../Models/IPreAuthorisable";
import { IAuthorisable } from "../Models/IAuthorisable";

export default class AuthorisationVM extends Views.ViewModelBase {

    private dataCache = AppService.get(DomainTypes.Services.DataCache);

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private auditApiClient = AppService.get(DomainTypes.ApiClients.Audit),
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications),
        private authorisationService = AppService.get(Types.Neo.Security.AuthorisationService),
        private apiClient = AppService.get(Types.Domain.ApiClients.Config)
    ) {
        super(taskRunner);

        this.makeObservable();
    }

    public auditVM = this.registerViewModel(Audit.AuditVM);

    public config: ConfigValues | null = null;

    public showPreAuthoriseButton = false;

    public showAuthoriseButton = false;

    private currentRecord?: IPreAuthorisable | IAuthorisable;
    
    public onDelete?: () => void;
    
    private prevCriteria?: { fromDate: Date | null, toDate: Date | null };
    
    public get isAuthorisingDelete(): boolean { return this.currentRecord != null && this.isIAuthorisable(this.currentRecord) && this.currentRecord.isPendingDelete; }

    private isIAuthorisable(record: IPreAuthorisable | IAuthorisable): record is IAuthorisable {
        return (record as IAuthorisable).authorisation !== undefined;
    }

    private isIPreAuthorisable(record: IPreAuthorisable | IAuthorisable): record is IPreAuthorisable {
        return (record as IPreAuthorisable).preAuthorisation !== undefined;
    }

    public async viewAuditHistory(
        record: IAuthorisable | IPreAuthorisable,
        preAuthoriseRoleName: string | undefined,
        authoriseRoleName: string,
        onDelete: () => void) {

        let history: Model.PlainObject<Audit.AuditEntityLookup>[];
        await this.loadConfig();

        if (this.isIAuthorisable(record) && record.authorisation.isAuthorised) {
            this.showAuthoriseButton = false;

            const criteria = await this.auditVM.showCriteria(record.constructor.name.readable(), this.prevCriteria);
            this.prevCriteria = { fromDate: criteria.fromDate, toDate: criteria.toDate };

            history = await this.taskRunner.waitFor(this.auditApiClient.getChangesForModel(record, criteria))

        } else {
            if (this.config?.preAuthEnabled && this.isIPreAuthorisable(record)) {
                if (record.preAuthorisation.isAuthorised) {
                    this.showAuthoriseButton = this.authorisationService.hasRole(authoriseRoleName);
                }
                else {
                    if (!preAuthoriseRoleName) {
                        throw "Pre-authorisation role name is required";
                    }
                    this.showPreAuthoriseButton = this.authorisationService.hasRole(preAuthoriseRoleName);
                }
            }
            else {
                this.showAuthoriseButton = this.authorisationService.hasRole(authoriseRoleName);
            }
            history = await this.taskRunner.waitFor(this.auditApiClient.getChangesSinceLastAuthorised(record));
        }

        this.onDelete = onDelete;
        this.currentRecord = record;
        this.auditVM.showAuditHistory(history);
    }

    public async loadConfig() {
        this.config = await this.dataCache.tenantConfig.getDataAsync();
    }

    public authorise() {
        return this.taskRunner.run(async () => {
            if (this.currentRecord) {
                if (this.config?.preAuthEnabled && this.currentRecord && this.isIPreAuthorisable(this.currentRecord) && !this.currentRecord.preAuthorisation.isAuthorised) {
                    await this.auditApiClient.preAuthoriseRecord(this.currentRecord);
                    this.currentRecord.preAuthorisation.isAuthorised = true;
                    this.currentRecord.preAuthorisation.isSelfDirty = false;
                    this.showPreAuthoriseButton = false;
                    this.showAuthoriseButton = true;
                    this.notifications.addSuccess("Pre-Authorised", `${AuthorisationVM.getTypeName(this.currentRecord.constructor)} has been pre-authorised`, NotificationDuration.Standard);
                }
                else if (this.isIAuthorisable(this.currentRecord)) {
                    if (!this.onDelete) {
                        throw "No delete handler defined";
                    }
                    
                    var authoriseResponse = await this.auditApiClient.authoriseRecord(this.currentRecord);
                    this.currentRecord.authorisation.isAuthorised = true;
                    this.currentRecord.authorisation.isSelfDirty = false;

                    if (authoriseResponse.data === true) {
                        this.notifications.addSuccess("Deleted", `${AuthorisationVM.getTypeName(this.currentRecord.constructor)} has been deleted`, NotificationDuration.Standard);
                        this.onDelete();
                    }
                    else {
                        this.notifications.addSuccess("Authorised", `${AuthorisationVM.getTypeName(this.currentRecord.constructor)} has been authorised`, NotificationDuration.Standard);
                    }
                }
            }
            this.auditVM.show = false;
        });
    }

    public static getTypeName(type: Function) {
        return ((type as any).typeName ?? type.name).readable();
    }
}